import { makeAutoObservable, runInAction } from "mobx";
import { IPolicyRule, IReturnReason } from "../../../models/ReturnReasonTypes";
import ReturnReasonsService from "../../../services/api/return-reasons-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import {ISaveable, WithOld} from "../../../stores/types";
import { GLOBAL_E_REASON_CODE_MAX_LENGTH, RETURN_REASON_TYPE } from "../constants";
import FollowUpQuestionStore from "./follow-up-question-store";
import { IReasonListStore } from "./reason-list-store";
import { generateQuestion } from "./follow-up-question-model";
import { uuid } from "../../../services/helpers";
import { ITranslationScopeContext } from "../../../translation/translation-scope";
import translationStore from "../../../translation/stores/translations-store";
import { trackShopperTextObjectSaved } from "../../../translation/metrics/translations-mixpanel";
import { IInputValitationResponse } from "../../../hooks/input-validation-hook";


interface IReturnReasonChanges extends Partial<IReturnReason>{};

class ReasonStore implements ISaveable {
    private static readonly GLOBAL_E_CODE_VALIDATION = {
        DUPLICATE: 'GLOBAL_E_REASON_CODE_MUST_BE_UNIQUE',
        MAX_LENGTH: 'GLOBAL_E_REASON_CODE_MAX_LENGTH',
        EMPTY: 'GLOBAL_E_REASON_CODE_CANNOT_BE_EMPTY',
        WHITESPACE_ONLY: 'GLOBAL_E_REASON_CODE_CANNOT_BE_EMPTY',
    } as const;
    private _changes: IReturnReasonChanges = {};
    private _model: IReturnReason = {} as IReturnReason;
    private _fuQuestions: FollowUpQuestionStore[] = [];
    private _isSaving: boolean = false;
    private _clientId?: string;

    private _currentTranslationKeys: string[] = [];

    private readonly _translatableFields: string[] = [
        'displayName'
    ];

    constructor(
        private _reasonListStore: IReasonListStore,
        private _discardReason: (store: ReasonStore) => void,
        model: IReturnReason = {} as IReturnReason,
        clientId?: string
    ) {
        this._clientId = clientId;
        this._discardQuestion = this._discardQuestion.bind(this);
        this._setModel(model);
        makeAutoObservable(this);
    }

    private get returnReasonsService(): ReturnReasonsService {
		return rootContainer.get<ReturnReasonsService>(TYPE.ReturnReasonsService);
	}

    private _setModel(model: IReturnReason): void {
        this._model = model;
        this._fuQuestions = this._model.follow_up_questions?.map(questionModel => {
            const question = new FollowUpQuestionStore(this._model.id, this._discardQuestion, questionModel);
            return question;
        }) ?? [];
        this._changes = {} as IReturnReasonChanges;
    }

    public setChanges(changes: IReturnReasonChanges): void {
        this._changes = changes;
        this._fuQuestions = this._changes.follow_up_questions?.map(questionModel => {
            const question = new FollowUpQuestionStore(this.id, this._discardQuestion);
            question.setChanges(questionModel);
            return question;
        }) ?? [];
    }

    public isIdEquals(id?:string|number): boolean {
        return this._model.id === Number(id) || this._clientId === (id ?? '').toString();
    }

    public get followUpQuestions() { return this._fuQuestions.slice().sort((a, b) => a.weight - b.weight); }
    public get type(): RETURN_REASON_TYPE { return this._changes.type ?? this._model.type; }
    public get displayName() { return this._changes.display_name ?? this._model.display_name; }
    public get externalReasonCode() { return this._changes.external_reason_code ?? this._model.external_reason_code; }
    public get globalECode() { return this._changes.global_e_code ?? this._model.global_e_code; }
    /** Last saved code from the API (ignores unsaved edits in `_changes`). */
    public get savedGlobalECode() { return this._model.global_e_code; }
    /**
     * True when the server assigned the Global-E reason code (not typed by the merchant).
     * Used for the "Auto-generated" badge. Local edits set the draft flag to false in `setGlobalECode`;
     * after discard or save the getter falls back to `_model` again.
     */
    public get globalECodeIsAutoGenerated(): boolean {
        return this._changes.global_e_code_is_auto_generated
            ?? this._model.global_e_code_is_auto_generated
            ?? false;
    }
    public get id(): number|string { return this._model.id ?? this._clientId; }
    /** True after the reason exists on the server (numeric id). New reasons only have a client id in the URL. */
    public get isSavedOnServer(): boolean { return typeof this._model.id === 'number'; }
    public get isEnabled() { return this._changes.is_enabled ?? this._model.is_enabled; }
    public get weight() { return this._changes.weight ?? this._model.weight; }
    public get deleted() { return this._changes._deleted ?? false; }
    public get isSwitchToExchangeEnabled() { return this._changes.is_eligible_for_switch_to_exchange ?? this._model.is_eligible_for_switch_to_exchange; }
    public get exchangeTypes() { return this._changes.exchange_types ?? this._model.exchange_types; }
    public isSaving(): boolean { return this._isSaving; }

    public setType(val:RETURN_REASON_TYPE) {
        this._changes.type = val;
    }

    public setExternalReasonCode(val:string|undefined) {
        this._changes.external_reason_code = val;
    }

    /** Any local edit clears the auto-generated draft flag so the badge hides until discard/save. */
    public setGlobalECode(val:string|undefined) {
        this._changes.global_e_code = val;
        this._changes.global_e_code_is_auto_generated = false;
    }

    /** True when `global_e_code` was touched locally (including cleared to empty). */
    public hasGlobalECodeChanges(): boolean {
        return Object.prototype.hasOwnProperty.call(this._changes, 'global_e_code');
    }

    /**
     * Client-side checks for the Global-e reason code field.
     * Returns translation keys in `messages`; the UI resolves them via `getTranslation`.
     *
     * @param isReasonCodeEditMode — true when the merchant is editing the code (saved reasons start readonly until Edit).
     */
    public validateGlobalECode(isReasonCodeEditMode: boolean): IInputValitationResponse {
        const value = this.globalECode ?? '';

        // 1. Required on edit: an existing reason cannot be saved with a cleared code while the field is editable.
        //    New (unsaved) reasons may leave the code empty; the backend auto-generates one on create.
        if (this.isSavedOnServer && isReasonCodeEditMode && value === '') {
            return { isValid: false, messages: [ReasonStore.GLOBAL_E_CODE_VALIDATION.EMPTY] };
        }

        // 2. Reject whitespace-only values (e.g. spaces) so we never persist a non-empty but meaningless code.
        if (value.length > 0 && value.trim() === '') {
            return { isValid: false, messages: [ReasonStore.GLOBAL_E_CODE_VALIDATION.WHITESPACE_ONLY] };
        }

        // 3. Enforce the same max length as the API validator and the input `maxlength` attribute.
        if (value.length > GLOBAL_E_REASON_CODE_MAX_LENGTH) {
            return { isValid: false, messages: [ReasonStore.GLOBAL_E_CODE_VALIDATION.MAX_LENGTH] };
        }

        // 4. Uniqueness among this merchant's reasons (compare to saved codes, including other reasons' unsaved drafts).
        const duplicateExists = this._reasonListStore.returnReasons.some(other => {
            if (other.id === this.id) {
                return false;
            }

            const otherCode = other.savedGlobalECode ?? other.globalECode ?? '';
            return otherCode !== '' && otherCode === value;
        });

        // Empty code is allowed here for new reasons; duplicates are only checked for non-empty values.
        if (value !== '' && duplicateExists) {
            return { isValid: false, messages: [ReasonStore.GLOBAL_E_CODE_VALIDATION.DUPLICATE] };
        }

        return { isValid: true, messages: [] };
    }

    public setIsEnabled(val:boolean) {
        this._changes.is_enabled = val;
    }

    public setWeight(val:number) {
        this._changes.weight = val;
    }

    public setIsSwitchToExchangeEnabled(val:boolean) {
        this._changes.is_eligible_for_switch_to_exchange = val;
    }

    public setExchangeTypes(val: string) {
        this._changes.exchange_types = val;
    }

    public onExchangeTypeChange(exchangeType: string) {
        const exchangeTypesArr = this.exchangeTypes?.split(',') ?? [];
        const index = exchangeTypesArr.indexOf(exchangeType);
        if (index !== -1) {
            exchangeTypesArr.splice(index, 1);
        } else {
            exchangeTypesArr.push(exchangeType);
        }
        this.setExchangeTypes(exchangeTypesArr.join(','));
    }

    public getNewWeight() : number {
        if (this._fuQuestions.length === 0) {
            return 1;
        }

        return Math.max(...this._fuQuestions.map(q => q.weight)) + 1;
    }

    public setDeleted(setDeletedTranslationKeys: ITranslationScopeContext['setDeletedKeys']) {
        const keyList = this.getTranslationKeys();
        setDeletedTranslationKeys(keyList);

        this._changes._deleted = true;

        this._fuQuestions.forEach(store => {
            store.setDeleted(setDeletedTranslationKeys);
        });
    }

    public getTranslationKeys(): string[] {
        const keyList: string[] = [];

        /** commented out, since will be implemented with delete reason feature */
        if (this.deleted) {
            return keyList;
        }

        this._translatableFields.forEach(field => {
            const key = this[field as keyof ReasonStore];
            if (key) {
                keyList.push(key.toString());
            }
        });
        this._fuQuestions.forEach(qStore => {
            keyList.push(...qStore.getTranslationKeys());
        });

        /** we need this._currentTranslationKeys property to use it as dependency in react hooks,
         * so that the hook method will run only when the list was changed */
        if (this._currentTranslationKeys.length !== keyList.length || !this._currentTranslationKeys.every(k => !!keyList.find(key => key === k))) {
            runInAction(() => {
                this._currentTranslationKeys = keyList;
            });
        }

        return this._currentTranslationKeys;
    }

    public addFollowUpQuestion():string {
        const clientId = uuid();
        const newWeight = this.getNewWeight();

        const questionStore = new FollowUpQuestionStore(this.id, this._discardQuestion);
        const changesModel = generateQuestion(clientId, undefined, newWeight);
        questionStore.setChanges(changesModel);
        this._fuQuestions.push(questionStore);
        return clientId;
    }

    /** preventing from disabling last reason */
    public canDisable(): boolean {
        const enabledCount =  this._reasonListStore.returnReasons.filter(store => store.isEnabled).length;
        return enabledCount > 1;
    }

    /** ISaveable implementation */
    public hasChanges(): boolean {
        const hasQuestionsChanges = this._fuQuestions.some(store => store.hasChanges());
        return !!Object.keys(this._changes).length || hasQuestionsChanges;
    }

    private getUpdatedModel(): IReturnReason {
        return {
            ...{ ...this._model, ...this._changes },
            follow_up_questions: this._fuQuestions?.map(store => store.getUpdatedModel()),
        };
    }

    public async save(): Promise<void> {
        const updatedModel: WithOld<IReturnReason> = this.getUpdatedModel();
        console.log('save return reason', updatedModel);
        const action = updatedModel.id ? "update" : "create";

        let savedReason: IReturnReason|undefined = undefined;
        if (updatedModel.id) {
            updatedModel.__old = this._model;
            savedReason = await this.returnReasonsService.updateReason(updatedModel.id, updatedModel);
        } else {
            savedReason = await this.returnReasonsService.createReason(updatedModel);
        }

        runInAction(() => {
            this._setModel(savedReason as IReturnReason);
        });

        const scopeStore = translationStore.loadScope("return-reason");
        trackShopperTextObjectSaved({
            action,
            object_type: "return_reason",
            enabled_locale_count: scopeStore.enabledLocaleCount(),
            translation_debt_created_count: scopeStore.countMissingTranslations(this.getTranslationKeys()),
        });
    }

    public async delete(): Promise<IPolicyRule[]> {
        return await this.returnReasonsService.deleteReason(this._model.id);
    }

    public discardChanges():void {
        if (!this._model.id) {
            this._discardReason(this);
        }
        else {
            this._changes = {} as IReturnReason;
            [...this._fuQuestions].forEach(store => store.discardChanges());
        }
    }

    /** removes newly created question store after it was discarded */
    private _discardQuestion(store: FollowUpQuestionStore): void {
        const storeIndex = this._fuQuestions.findIndex(s => s === store);
        if (storeIndex < 0) {
            /** should never happen */
            throw new Error('discardQuestion did not find the store to remove');
        }
        this._fuQuestions.splice(storeIndex, 1);
    }

    public commitChanges() {
        this._model = {...this._model, ...this._changes};
        this._changes = {} as IReturnReason;
    }
}

export default ReasonStore;
